import {areSetsEqual, compact} from '@benchling/js-utils';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {
  ProcessFlowchartNodeDetailsType,
  ProcessFlowchartPlatformFeatureType,
  ProcessMapperTokenType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {LookupConfigSpec, LookupStepSpec, LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {
  findAllDownstreamMappableNodeIds,
  findAllUpstreamMappableNodeIds,
  isMappableNode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {isRouterNodeDetailsGQL} from 'processes/graph-builder-processes/flowchart-viz/util/getNodeDetailsTypeForGQL';
import {
  EdgeIdsToEdgesMap,
  makeRouterConfig,
} from 'processes/graph-builder-processes/flowchart-viz/util/makeProcessFlowchartInteractiveNode';
import {defaultNodeHandleIdGenerator} from 'ui-library/reactflow/nodes/SimpleBenchlingNode/sub-components/SimpleBenchlingNodeHandle';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';
import {assertNever} from 'util/Types';

import {ProcessTaskDefinitionGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessTaskDefinitionGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {makeDataFlowEdgeHandle, getFieldInfoFromDataFlowEdgeHandle} from './flowchartIdentifierUtils';
import isLookupConfigCustomMapping from './isLookupConfigCustomMapping';
import {convertRootRowToMapFromStartRow} from './mapFromStartUtils';
import {
  ProcessFlowchartEdgeType,
  FlowchartStaticEdgeData,
  CustomMappingData,
  TaskSchemaNodeSectionType,
  isCustomMappingDataEmpty,
  TransitionSource,
} from './processFlowchartConfigEditorConstants';
import {
  ProcessTaskDefinitionVersionGraphQL,
  ProcessFlowchartConfigVersionGQL,
  FieldDefinitionGraphQL,
  FieldMapperConfigGQL,
  InboundSchemaMapperGQL,
  SchemaMapperDependencyGQL,
} from './processFlowchartConfigEditorConstants';
import {
  StaticFlowchartNodeData,
  RouterStaticNodeData,
  TaskSchemaStaticNodeData,
  isStaticTaskSchemaNodeData,
  TaskSchemaNodeRowData,
} from './processFlowchartConfigEditorNodeTypes';
import {
  createStaticNodeDataFromRouterConfig,
  createEndNodeStaticData,
  createStaticNodeDataFromTaskDefinitionVersion,
} from './processFlowchartConfigEditorUtils';
import {getDefaultTransitionSource, getTransitionSourceByNodeIdPairKey} from './TransitionSourceUtils';
import {getFieldPairKey, getOrCreateTransition} from './types/transition/transitionKeyUtils';
import {Transition} from './types/transition/TransitionTypes';

export function generateDeterministicEdgeId(sourceNodeId: string, targetNodeId: string): string {
  return `edge-${sourceNodeId}-${targetNodeId}`;
}

export function buildControlFlowEdge({
  clientId,
  sourceNodeId,
  targetNodeId,
  label,
}: {
  clientId?: string;
  sourceNodeId: string;
  targetNodeId: string;
  label?: string;
}): FlowchartStaticEdgeData {
  return {
    id: clientId ?? generateDeterministicEdgeId(sourceNodeId, targetNodeId),
    source: sourceNodeId,
    target: targetNodeId,
    sourceHandle: defaultNodeHandleIdGenerator(sourceNodeId, 'source'),
    targetHandle: defaultNodeHandleIdGenerator(targetNodeId, 'target'),
    type: ProcessFlowchartEdgeType.CONTROL_FLOW,
    label,
  };
}

export function buildDataFlowEdge({
  sourceNodeId,
  targetNodeId,
  fromFieldId,
  fromFieldSectionType,
  toFieldId,
  toFieldSectionType,
}: {
  sourceNodeId: string;
  targetNodeId: string;
  fromFieldId: string;
  fromFieldSectionType: TaskSchemaNodeSectionType;
  toFieldId: string;
  toFieldSectionType: TaskSchemaNodeSectionType;
}): FlowchartStaticEdgeData {
  return {
    id: uuidv4(),
    source: sourceNodeId,
    target: targetNodeId,
    sourceHandle: makeDataFlowEdgeHandle({
      nodeId: sourceNodeId,
      fieldClientId: fromFieldId,
      sectionType: fromFieldSectionType,
      handleType: 'source',
    }),
    targetHandle: makeDataFlowEdgeHandle({
      nodeId: targetNodeId,
      fieldClientId: toFieldId,
      sectionType: toFieldSectionType,
      handleType: 'target',
    }),
    type: ProcessFlowchartEdgeType.DATA_FLOW,
  };
}

/**
 * Get the first field id from a lookup config with a SCHEMA_FIELD or PROCESS_TASK_DEFINITION_FIELD lookup step.
 *
 * @param lookupConfig The lookup config to get the first field id from.
 * @returns The first field id from the lookup config, or null if no field id is found.
 */

export function getFirstFieldIdFromLookupConfig(lookupConfig: LookupConfigSpec): string | null {
  const lookupStep = lookupConfig.lookupSteps.find(
    (lookupStep) =>
      lookupStep.type === LookupStepType.SCHEMA_FIELD ||
      lookupStep.type === LookupStepType.PROCESS_TASK_DEFINITION_FIELD
  );
  if (lookupStep == null) {
    return null;
  }

  if (lookupStep.type === LookupStepType.SCHEMA_FIELD) {
    if (lookupStep.schemaField == null || !APIUtil.isSchemaField(lookupStep.schemaField)) {
      return null;
    }
    return lookupStep.schemaField;
  } else {
    return lookupStep.processTaskDefinitionFieldIdentifier;
  }
}

const getNodeIdPairKeyForEdgeLabel = (fromNodeId: string, toNodeId: string): string =>
  `${fromNodeId}-${toNodeId}`;

export interface StaticFlowchartData {
  nodesStaticData: Array<StaticFlowchartNodeData>;
  edgesStaticData: Array<FlowchartStaticEdgeData>;
  // TODO(BNCH-132872): Replace with some client side schema mapper config object when we enable ACM and PCM in the new
  // editor experience.
  schemaMapperConfigs: Array<never>;
  availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  transitions: Record<string, Transition>;
  // TODO(BNCH-141054): Remove these two fields once we migrate to PTDV-based transitions
  transitionSourceByNodeIdPair: Record<string, TransitionSource>;
  customMappingDataByEdgeId: Record<string, CustomMappingData>;
  didInitialConfigHaveConditionalMapping: boolean;
  hasChainedRouters: boolean;
  laBlockingReason: string | null;
  cidToIdMap: CidIdMap;
}

interface BuildStaticDataFromFlowchartConfigGraphQLRunnerArgs {
  flowchartConfigVersion: ProcessFlowchartConfigVersionGQL | null;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  rootTaskDefinition: ProcessTaskDefinitionGraphQL & {
    latestVersion: ProcessTaskDefinitionVersionGraphQL;
  };
  allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
}

class BuildStaticDataFromFlowchartConfigGraphQLRunner {
  private readonly flowchartConfigVersion: ProcessFlowchartConfigVersionGQL | null;
  private readonly rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  private readonly rootTaskDefinition: ProcessTaskDefinitionGraphQL & {
    latestVersion: ProcessTaskDefinitionVersionGraphQL;
  };
  private readonly allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  private readonly flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;

  private nodeClientIdToTaskDefinitionVersion: Record<string, ProcessTaskDefinitionVersionGraphQL>;

  private controlFlowEdges: Array<FlowchartStaticEdgeData>;
  private dataFlowEdges: Array<FlowchartStaticEdgeData>;
  private nodesStaticData: Array<StaticFlowchartNodeData>;
  private nodesById: Record<string, StaticFlowchartNodeData>;
  private edgesStaticData: Array<FlowchartStaticEdgeData>;
  private customMappingDataByEdgeId: Record<string, CustomMappingData>;
  private mapFromStartFieldIdsByNode: Record<string, Array<string>>;
  private transitionSourceByNodeIdPair: Record<string, TransitionSource>;
  private transitions: Record<string, Transition>;
  private nodeIdPairToEdgeLabel: Record<string, string>;
  private cidToIdMap: CidIdMap;

  // Maps from field identifier to client identifier for data flow edges
  private fieldIdentifierToFieldClientId: Record<string, string>;

  constructor(args: BuildStaticDataFromFlowchartConfigGraphQLRunnerArgs) {
    this.flowchartConfigVersion = args.flowchartConfigVersion;
    this.rootTaskSchema = args.rootTaskSchema;
    this.rootTaskDefinition = args.rootTaskDefinition;
    this.allTaskSchemas = args.allTaskSchemas;
    this.flowchartEditorConfigurator = args.flowchartEditorConfigurator;

    this.cidToIdMap = {};

    this.nodeClientIdToTaskDefinitionVersion = {};
    this.controlFlowEdges = [];
    this.dataFlowEdges = [];
    this.fieldIdentifierToFieldClientId = {};

    this.nodesStaticData = [];
    this.nodesById = {};
    this.edgesStaticData = [];
    this.customMappingDataByEdgeId = {};
    this.mapFromStartFieldIdsByNode = {};
    this.transitionSourceByNodeIdPair = {};
    this.transitions = {};
    this.nodeIdPairToEdgeLabel = {};
  }

  run(): StaticFlowchartData {
    this.extractTaskDefinitionVersions();
    this.buildFieldIdentifierToClientIdMapping();
    this.convertFlowchartConfigVersionToNodes();
    this.buildNodesById();
    this.convertFlowchartConfigVersionToEdges();
    this.setFallbackTransitionSources();
    const hasChainedRouters = this.getHasChainedRouters();
    const laBlockingReason = this.getLABlockingReason();

    return {
      nodesStaticData: this.nodesStaticData,
      edgesStaticData: this.edgesStaticData,
      customMappingDataByEdgeId: this.customMappingDataByEdgeId,
      schemaMapperConfigs: [],
      availableTaskSchemas: accessibleObjs(this.flowchartConfigVersion?.availableSchemas ?? []),
      transitionSourceByNodeIdPair: this.transitionSourceByNodeIdPair,
      transitions: this.transitions,
      didInitialConfigHaveConditionalMapping: this.getDidInitialConfigHaveConditionalMapping(),
      hasChainedRouters,
      laBlockingReason,
      cidToIdMap: this.cidToIdMap,
    };
  }

  /**
   * For single-source requests, checks whether an existing transition (e.g. a merging transition)
   * already covers this source-target pair before creating a new one. This works because we do not allow
   * transitions that are a subset of each other.
   */
  private getOrCreateTransition(sourcePTDVIds: Array<string>, targetPTDVId: string): Transition {
    if (sourcePTDVIds.length === 1) {
      for (const transition of Object.values(this.transitions)) {
        if (transition.targetPTDVId === targetPTDVId && transition.sourcePTDVIds.includes(sourcePTDVIds[0])) {
          return transition;
        }
      }
    }

    return getOrCreateTransition({
      sourcePTDVIds,
      targetPTDVId,
      transitions: this.transitions,
      isPlanned: true,
    }).transition;
  }

  private buildNodesById(): void {
    for (const node of this.nodesStaticData) {
      this.nodesById[node.id] = node;
    }
  }

  private getNodeById(nodeId: string): StaticFlowchartNodeData | undefined {
    return this.nodesById[nodeId];
  }

  private getTaskSchemaNodeById(nodeId: string): TaskSchemaStaticNodeData | undefined {
    const node = this.nodesById[nodeId];
    return node && isStaticTaskSchemaNodeData(node) ? node : undefined;
  }

  private getRootNode(): TaskSchemaStaticNodeData | undefined {
    return this.nodesStaticData.find(
      (node): node is TaskSchemaStaticNodeData =>
        isStaticTaskSchemaNodeData(node) &&
        node.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    );
  }

  private getHasChainedRouters(): boolean {
    for (const edge of this.flowchartConfigVersion?.templateFlowchart?.edgeConfigs ?? []) {
      const sourceNode = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs.find(
        (node) => node.id === edge.fromNodeConfig.id
      );
      const targetNode = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs.find(
        (node) => node.id === edge.toNodeConfig.id
      );
      if (sourceNode == null || targetNode == null) {
        continue;
      }
      if (isRouterNodeDetailsGQL(sourceNode.nodeDetails) && isRouterNodeDetailsGQL(targetNode.nodeDetails)) {
        return true;
      }
    }
    return false;
  }

  private getHasUndrawnMapperConfigs(): boolean {
    const schemaMapperConfigs = this.flowchartConfigVersion?.schemaMapperConfigs ?? [];
    const schemaMapperConfigPairs = new Set<string>();
    const controlFlowEdgePairs = new Set<string>();
    for (const schemaMapperConfig of schemaMapperConfigs) {
      if (isInaccessible(schemaMapperConfig.toProcessTaskDefinitionVersion)) {
        continue;
      }
      // Handle all source configs (supports merging transitions with multiple sources)
      for (const sourceConfig of schemaMapperConfig.schemaMapperSourceConfigs) {
        if (!isInaccessible(sourceConfig.processTaskDefinitionVersion)) {
          schemaMapperConfigPairs.add(
            `${sourceConfig.processTaskDefinitionVersion.id}-${schemaMapperConfig.toProcessTaskDefinitionVersion.id}`
          );
        }
      }
    }

    for (const node of this.nodesStaticData.filter((node) => isMappableNode({id: node.id, data: node}))) {
      const upstreamMappableNodeIds = findAllUpstreamMappableNodeIds(
        {nodes: this.nodesStaticData.map((node) => ({id: node.id, data: node})), edges: this.edgesStaticData},
        node.id
      );
      for (const upstreamMappableNodeId of upstreamMappableNodeIds) {
        controlFlowEdgePairs.add(
          `${this.nodeClientIdToTaskDefinitionVersion[upstreamMappableNodeId].id}-${this.nodeClientIdToTaskDefinitionVersion[node.id].id}`
        );
      }
    }
    if (!areSetsEqual(schemaMapperConfigPairs, controlFlowEdgePairs)) {
      this.logLABlockingReason('undrawn mapper configs');
    }
    return !areSetsEqual(schemaMapperConfigPairs, controlFlowEdgePairs);
  }

  /**
   * Merging transitions are partly supported, but should still be blocked since we don't have full support yet.
   */
  private hasMergingTransitions(): boolean {
    for (const schemaMapperConfig of this.flowchartConfigVersion?.schemaMapperConfigs ?? []) {
      if (schemaMapperConfig.schemaMapperSourceConfigs.length > 1) {
        this.logLABlockingReason('merging transitions');
        return true;
      }
    }
    return false;
  }

  private logLABlockingReason(reason: string): void {
    logging.warn('LA horizontal flowchart loading with unsupported feature.', {
      extra: {
        flowchartConfigVersionId: this.flowchartConfigVersion?.id,
        rootTaskSchemaId: this.rootTaskSchema.id,
        feature: reason,
      },
    });
  }

  private getLABlockingReason(): string | null {
    if (this.hasMergingTransitions() || this.getHasUndrawnMapperConfigs()) {
      return 'This flowchart isn’t editable in the limited release of the new configuration experience. Editing will be available when the new experience is generally available.';
    }
    return null;
  }

  private extractTaskDefinitionVersions(): void {
    // TODO: See if this can be moved into the convertGQLFlowchartConfigToClientFlowchart function
    // so that we don't have to extract the task definition versions first
    const nodeConfigs = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs ?? [];
    for (const nodeConfig of nodeConfigs) {
      const nodeDetails = nodeConfig.nodeDetails;
      if (
        nodeDetails.__typename === 'ProcessTaskNodeDetailsNode' ||
        nodeDetails.__typename === 'ProcessRootNodeDetailsNode' ||
        nodeDetails.__typename === 'ProcessOutputNodeDetailsNode'
      ) {
        const processTaskDefinitionVersion = nodeDetails.processTaskDefinitionVersion;
        if (isInaccessible(processTaskDefinitionVersion)) {
          continue;
        }
        this.nodeClientIdToTaskDefinitionVersion[nodeConfig.id] = processTaskDefinitionVersion;
      }
    }
  }

  /**
   * Builds a mapping from field identifiers to their client identifiers, to be used when forming
   * data flow edges.
   */
  private buildFieldIdentifierToClientIdMapping(): void {
    const nodeConfigs = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs ?? [];
    for (const nodeConfig of nodeConfigs) {
      const taskDefinitionVersion = this.nodeClientIdToTaskDefinitionVersion[nodeConfig.id];
      if (taskDefinitionVersion == null) {
        continue;
      }

      const inputFieldDefinitions = taskDefinitionVersion.inputFieldDefinitions ?? [];
      for (const fieldDefinition of inputFieldDefinitions) {
        this.fieldIdentifierToFieldClientId[fieldDefinition.fieldIdentifier] =
          fieldDefinition.fieldClientIdentifier;
      }

      const outputFieldDefinitions = taskDefinitionVersion.outputFieldDefinitions ?? [];
      for (const fieldDefinition of outputFieldDefinitions) {
        this.fieldIdentifierToFieldClientId[fieldDefinition.fieldIdentifier] =
          fieldDefinition.fieldClientIdentifier;
      }
    }
  }

  private convertFlowchartConfigVersionToNodes(): void {
    if (this.flowchartConfigVersion?.templateFlowchart == null) {
      const rootTaskDefinitionVersion = this.rootTaskDefinition.latestVersion;
      this.nodesStaticData = [
        createStaticNodeDataFromTaskDefinitionVersion({
          nodeClientId: 'root',
          taskDefinitionVersion: rootTaskDefinitionVersion,
          nodeDetailsType: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
          allTaskSchemas: this.allTaskSchemas,
        }),
      ];
      if (this.rootTaskSchema.outputSchema != null) {
        const outputNode = createStaticNodeDataFromTaskDefinitionVersion({
          nodeClientId: 'output',
          taskDefinitionVersion: rootTaskDefinitionVersion,
          nodeDetailsType: ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
          allTaskSchemas: this.allTaskSchemas,
        });
        this.nodesStaticData.push(outputNode);
      }
    } else {
      const existingNodeNames: Array<string> = [];

      this.nodesStaticData = compact(
        this.flowchartConfigVersion?.templateFlowchart.nodeConfigs.map((node) => {
          let clientNode: StaticFlowchartNodeData | null = null;
          switch (node.nodeDetails.__typename) {
            case 'ProcessRootNodeDetailsNode':
            case 'ProcessOutputNodeDetailsNode':
            case 'ProcessTaskNodeDetailsNode': {
              const taskDefinitionVersion = node.nodeDetails.processTaskDefinitionVersion;
              assert(!isInaccessible(taskDefinitionVersion), 'Task definition version is inaccessible');
              clientNode = createStaticNodeDataFromTaskDefinitionVersion({
                nodeClientId: node.id,
                taskDefinitionVersion,
                nodeDetailsType: node.nodeDetails.type,
                allTaskSchemas: this.allTaskSchemas,
                existingNodeNames,
              });
              break;
            }
            case 'ProcessRouterNodeDetailsNode': {
              const edgeIdsToEdges: EdgeIdsToEdgesMap = {};
              for (const edgeConfig of this.flowchartConfigVersion?.templateFlowchart?.edgeConfigs ?? []) {
                if (edgeIdsToEdges[edgeConfig.id] == null) {
                  edgeIdsToEdges[edgeConfig.id] = [];
                }
                edgeIdsToEdges[edgeConfig.id].push({
                  clientId: edgeConfig.id,
                  fromNodeClientId: edgeConfig.fromNodeConfig.id,
                  toNodeClientId: edgeConfig.toNodeConfig.id,
                });
              }

              const routerConfig = makeRouterConfig(
                node.nodeDetails.name,
                node.nodeDetails.routerFunctions,
                edgeIdsToEdges
              );
              const routerNode = createStaticNodeDataFromRouterConfig(node.id, routerConfig);
              this.extractEdgeLabels(routerNode);
              clientNode = routerNode;
              break;
            }
            case 'ProcessEndNodeDetailsNode':
              clientNode = createEndNodeStaticData(node.id, node.name);
              break;
            default:
              assert(false, `Unexpected node type ${assertNever(node.nodeDetails)}`);
              return null;
          }
          if (clientNode == null) {
            return null;
          }
          this.cidToIdMap[node.id] = clientNode.id;
          // Track node name for subsequent incrementing
          existingNodeNames.push(clientNode.nodeName);
          return clientNode;
        })
      );
    }
  }

  private extractEdgeLabels(node: RouterStaticNodeData): void {
    for (const functionSpec of node.routerConfig.functionSpecs) {
      const fromNodeId = functionSpec.edgeConfigReference?.fromNodeClientId;
      const toNodeId = functionSpec.edgeConfigReference?.toNodeClientId;
      if (fromNodeId == null || toNodeId == null) {
        continue;
      }
      this.nodeIdPairToEdgeLabel[getNodeIdPairKeyForEdgeLabel(fromNodeId, toNodeId)] = functionSpec.name;
    }
    if (node.routerConfig.defaultFunctionSpec != null) {
      const fromNodeId = node.routerConfig.defaultFunctionSpec.edgeConfigReference?.fromNodeClientId;
      const toNodeId = node.routerConfig.defaultFunctionSpec.edgeConfigReference?.toNodeClientId;
      if (fromNodeId != null && toNodeId != null) {
        this.nodeIdPairToEdgeLabel[getNodeIdPairKeyForEdgeLabel(fromNodeId, toNodeId)] =
          node.routerConfig.defaultFunctionSpec.name;
      }
    }
  }

  private convertFlowchartConfigVersionToEdges(): void {
    this.controlFlowEdges = this.buildControlFlowEdges();
    this.dataFlowEdges = this.buildDataFlowEdges();
    // Filter out data flow edges with nonexistent handles
    this.dataFlowEdges = this.dataFlowEdges.filter((edge) => this.doesDataFlowEdgeHaveValidHandles(edge));
    this.edgesStaticData = [...this.controlFlowEdges, ...this.dataFlowEdges];
    if (
      this.flowchartEditorConfigurator.hasFeature(ProcessFlowchartPlatformFeatureType.ALLOW_MAP_FROM_START)
    ) {
      this.updateNodesWithMapFromStartFields();
    }
  }

  /**
   * Check if a data flow edge's handles exist on the source and target nodes.
   */
  private doesDataFlowEdgeHaveValidHandles(edge: FlowchartStaticEdgeData): boolean {
    if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW || !edge.sourceHandle || !edge.targetHandle) {
      return false;
    }

    const sourceNode = this.getNodeById(edge.source);
    const targetNode = this.getNodeById(edge.target);

    if (
      !sourceNode ||
      !targetNode ||
      !isStaticTaskSchemaNodeData(sourceNode) ||
      !isStaticTaskSchemaNodeData(targetNode)
    ) {
      return false;
    }

    const sourceHandleInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
    const targetHandleInfo = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

    return (
      this.doesFieldExistOnNode(sourceNode, sourceHandleInfo.fieldClientId, sourceHandleInfo.sectionType) &&
      this.doesFieldExistOnNode(targetNode, targetHandleInfo.fieldClientId, targetHandleInfo.sectionType)
    );
  }

  /**
   * Check if a field exists on a node's field definitions and is not archived.
   */
  private doesFieldExistOnNode(
    node: TaskSchemaStaticNodeData,
    fieldClientId: string,
    sectionType: TaskSchemaNodeSectionType
  ): boolean {
    const isOutputNode = node.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
    const isOutputSection = sectionType === TaskSchemaNodeSectionType.OUTPUTS;
    const isMapFromStartSection = sectionType === TaskSchemaNodeSectionType.MAP_FROM_START;

    if (isMapFromStartSection) {
      const rootNode = this.getRootNode();
      if (!rootNode) {
        return false;
      }
      const rootFieldDefinitions = rootNode.taskDefinitionVersionSpec.inputFieldDefinitions;
      if (!rootFieldDefinitions) {
        return false;
      }
      return rootFieldDefinitions.some(
        (field) =>
          field.fieldClientIdentifier === fieldClientId &&
          (field.fieldDefinition.archiveData == null || !field.fieldDefinition.archiveData.archived)
      );
    }

    const fieldDefinitions =
      isOutputNode || isOutputSection
        ? node.taskDefinitionVersionSpec.outputFieldDefinitions
        : node.taskDefinitionVersionSpec.inputFieldDefinitions;

    if (!fieldDefinitions) {
      return false;
    }

    // Check that the field exists and is not archived
    return fieldDefinitions.some(
      (field) =>
        field.fieldClientIdentifier === fieldClientId &&
        (field.fieldDefinition.archiveData == null || !field.fieldDefinition.archiveData.archived)
    );
  }

  private buildControlFlowEdges(): Array<FlowchartStaticEdgeData> {
    return (
      this.flowchartConfigVersion?.templateFlowchart?.edgeConfigs.map((edge) => {
        const newEdge = buildControlFlowEdge({
          clientId: edge.id,
          sourceNodeId: edge.fromNodeConfig.id,
          targetNodeId: edge.toNodeConfig.id,
          label:
            this.nodeIdPairToEdgeLabel[
              getNodeIdPairKeyForEdgeLabel(edge.fromNodeConfig.id, edge.toNodeConfig.id)
            ],
        });
        this.cidToIdMap[edge.id] = edge.id;
        return newEdge;
      }) ?? []
    );
  }

  private buildDataFlowEdges(): Array<FlowchartStaticEdgeData> {
    const nodeConfigs = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs ?? [];
    const dataFlowEdges: Array<FlowchartStaticEdgeData> = [];
    for (const nodeConfig of nodeConfigs) {
      for (const inboundSchemaMapper of nodeConfig.inboundSchemaMappers) {
        const mergingTransition = this.getOrCreateMergingTransitionForSchemaMapper(
          inboundSchemaMapper,
          nodeConfig.id
        );

        for (const schemaMapperDependency of inboundSchemaMapper.schemaMapperDependencies ?? []) {
          // If the dependency has a source config, we need to initialize the "transition source" (aka the
          // control flow token) based on the source.
          // This allows us to accurately set the transition source for mappers that do not involve any field
          // mapping.
          if (schemaMapperDependency.schemaMapperSourceConfig != null) {
            const targetNodeId = nodeConfig.id;
            const sourceNodeId = schemaMapperDependency.fromNodeConfig.id;
            this.updateTransitionSourceByNodeIdPair({
              sourceNodeId,
              targetNodeId,
              mapperSource: schemaMapperDependency.schemaMapperSourceConfig,
            });
          }

          dataFlowEdges.push(
            ...compact(
              (schemaMapperDependency.fieldMapperConfigs ?? []).map((fieldMapperConfig) =>
                this.buildDataFlowEdgeFromFieldMapperConfig(
                  fieldMapperConfig,
                  schemaMapperDependency,
                  inboundSchemaMapper,
                  mergingTransition
                )
              )
            )
          );
        }
      }
    }
    return dataFlowEdges;
  }

  /**
   * Creates or retrieves a merging transition for a schema mapper config with multiple sources.
   * Returns null if the config has only one source (for single-source transitions) or cannot be found.
   */
  private getOrCreateMergingTransitionForSchemaMapper(
    inboundSchemaMapper: InboundSchemaMapperGQL,
    targetNodeId: string
  ): Transition | null {
    const schemaMapperConfigRef = inboundSchemaMapper.schemaMapperConfig;
    if (!schemaMapperConfigRef) {
      return null;
    }

    const fullSchemaMapperConfig = this.flowchartConfigVersion?.schemaMapperConfigs.find(
      (config) => config.id === schemaMapperConfigRef.id
    );
    if (!fullSchemaMapperConfig || fullSchemaMapperConfig.schemaMapperSourceConfigs.length <= 1) {
      return null;
    }

    const targetPTDVId = this.nodeClientIdToTaskDefinitionVersion[targetNodeId]?.id;
    if (!targetPTDVId) {
      return null;
    }

    const sourcePTDVIds: Array<string> = [];
    for (const sourceConfig of fullSchemaMapperConfig.schemaMapperSourceConfigs) {
      if (!isInaccessible(sourceConfig.processTaskDefinitionVersion)) {
        sourcePTDVIds.push(sourceConfig.processTaskDefinitionVersion.id);
      }
    }

    if (sourcePTDVIds.length <= 1) {
      return null;
    }

    return this.getOrCreateTransition(sourcePTDVIds, targetPTDVId);
  }

  private buildDataFlowEdgeFromFieldMapperConfig(
    fieldMapperConfig: FieldMapperConfigGQL,
    schemaMapperDependency: SchemaMapperDependencyGQL,
    inboundSchemaMapper: InboundSchemaMapperGQL,
    mergingTransition: Transition | null = null
  ): FlowchartStaticEdgeData | null {
    const fromNodeId = schemaMapperDependency.fromNodeConfig.id;
    const toNodeId = inboundSchemaMapper.toNodeConfig.id;

    const fromFieldInfo = this.getUpstreamMappingInfoFromFieldMapperConfig(fieldMapperConfig);
    if (fromFieldInfo == null) {
      return null;
    }
    if (fromFieldInfo.fromFieldSectionType === TaskSchemaNodeSectionType.MAP_FROM_START) {
      if (this.mapFromStartFieldIdsByNode[fromNodeId]) {
        this.mapFromStartFieldIdsByNode[fromNodeId].push(fromFieldInfo.fromFieldId);
      } else {
        this.mapFromStartFieldIdsByNode[fromNodeId] = [fromFieldInfo.fromFieldId];
      }
    }

    const mapsToType = inboundSchemaMapper.schemaMapperConfig?.mapsToType;
    if (mapsToType == null) {
      return null;
    }

    const toFieldSectionType =
      mapsToType === ProcessMapperTokenType.PROCESS_OUTPUT
        ? TaskSchemaNodeSectionType.OUTPUTS
        : TaskSchemaNodeSectionType.INPUTS;
    const toFieldId = fieldMapperConfig.toProcessFieldDefinition.fieldIdentifier;
    const toFieldClientId = this.fieldIdentifierToFieldClientId[toFieldId];

    const {fromFieldSectionType, fromFieldId} = fromFieldInfo;
    const fromFieldClientId = this.fieldIdentifierToFieldClientId[fromFieldId];

    const edge = buildDataFlowEdge({
      sourceNodeId: fromNodeId,
      targetNodeId: toNodeId,
      fromFieldId: fromFieldClientId,
      fromFieldSectionType,
      toFieldId: toFieldClientId,
      toFieldSectionType,
    });

    // This is populated in the case that we're not using a direct mapping.
    // Use the original field ID for custom mapping data
    const sourcePTDVId = this.nodeClientIdToTaskDefinitionVersion[fromNodeId]?.id;
    const targetPTDVId = this.nodeClientIdToTaskDefinitionVersion[toNodeId]?.id;

    if (sourcePTDVId && targetPTDVId) {
      // Use the merging transition if provided, otherwise create/get a single-source transition
      const transition = mergingTransition ?? this.getOrCreateTransition([sourcePTDVId], targetPTDVId);

      const transitionSource = this.getTransitionSourceFromMapperTokenType(fieldMapperConfig.mapsFromType);
      this.setTransitionSource(transition, sourcePTDVId, transitionSource);

      const fieldPairKey = getFieldPairKey({
        sourcePTDVId,
        sourceSectionType: fromFieldSectionType,
        sourceFieldClientId: fromFieldClientId,
        targetPTDVId,
        targetSectionType: toFieldSectionType,
        targetFieldClientId: toFieldClientId,
      });

      const customMappingData = this.buildCustomMappingData(fieldMapperConfig, fromFieldId);
      if (customMappingData) {
        this.customMappingDataByEdgeId[edge.id] = customMappingData;
      }
      transition.customMappingDataByFieldPair[fieldPairKey] = customMappingData;
    }

    this.updateTransitionSourceByNodeIdPair({
      sourceNodeId: fromNodeId,
      targetNodeId: toNodeId,
      mapperSource: fieldMapperConfig,
    });

    return edge;
  }

  private buildCustomMappingData(
    fieldMapperConfig: FieldMapperConfigGQL,
    fromFieldId: string
  ): CustomMappingData | null {
    const customMappingData: CustomMappingData = {};
    if (fieldMapperConfig.resultMustBeNonNullToMap) {
      customMappingData.resultMustBeNonNullToMap = true;
    }
    if (isLookupConfigCustomMapping(fieldMapperConfig.lookupConfig, fromFieldId)) {
      customMappingData.lookupConfig = {
        isMulti: fieldMapperConfig.lookupConfig.isMulti,
        lookupSteps: fieldMapperConfig.lookupConfig.lookupSteps,
        multiFlattenLevel: fieldMapperConfig.lookupConfig.multiFlattenLevel,
      };
    }
    return isCustomMappingDataEmpty(customMappingData) ? null : customMappingData;
  }

  private setTransitionSource(
    transition: Transition,
    sourcePTDVId: string,
    transitionSource: TransitionSource
  ): void {
    const existingSource = transition.transitionSourcesBySourcePTDVId[sourcePTDVId];
    if (existingSource != null && existingSource !== transitionSource) {
      assert(false, 'Expected existing transition source to match inferred transition source', {
        existingSource,
        transitionSource,
        sourcePTDVId,
      });
    }
    transition.transitionSourcesBySourcePTDVId[sourcePTDVId] = transitionSource;
  }

  private updateTransitionSourceByNodeIdPair({
    sourceNodeId,
    targetNodeId,
    mapperSource,
  }: {
    sourceNodeId: string;
    targetNodeId: string;
    mapperSource: {mapsFromType: ProcessMapperTokenType};
  }): void {
    const {mapsFromType} = mapperSource;
    const nodeIdPair = getTransitionSourceByNodeIdPairKey(sourceNodeId, targetNodeId);
    const existingTransitionSource = this.transitionSourceByNodeIdPair[nodeIdPair];
    const inferredTransitionSource = this.getTransitionSourceFromMapperTokenType(mapsFromType);
    assert(
      existingTransitionSource == null || existingTransitionSource === inferredTransitionSource,
      'Expected existing transition source to match inferred transition source',
      {
        existingTransitionSource,
        inferredTransitionSource,
        sourceNodeId,
        targetNodeId,
        mapsFromType,
      }
    );
    this.transitionSourceByNodeIdPair[nodeIdPair] = inferredTransitionSource;
  }

  private getUpstreamMappingInfoFromFieldMapperConfig(fieldMapperConfig: FieldMapperConfigGQL): {
    fromFieldSectionType: TaskSchemaNodeSectionType;
    fromFieldId: string;
  } | null {
    const fromTaskDefinitionVersion = fieldMapperConfig.fromProcessTaskDefinitionVersion;
    if (fromTaskDefinitionVersion == null || isInaccessible(fromTaskDefinitionVersion)) {
      return null;
    }
    const inputFieldDefinitions = fromTaskDefinitionVersion.inputFieldDefinitions ?? [];
    const outputFieldDefinitions = fromTaskDefinitionVersion.outputFieldDefinitions ?? [];

    // Examine lookup steps to find the field this edge is mapping from. Workflows after the introduction
    // of task definitions will have a PROCESS_TASK_DEFINITION_FIELD lookup step, while Workflows created
    // before that change will have a SCHEMA_FIELD lookup step.
    const fromFieldId = getFirstFieldIdFromLookupConfig(fieldMapperConfig.lookupConfig);
    if (fromFieldId == null) {
      return null;
    }

    const isFromFieldInFieldDefinitions = (fieldDefinitions: Array<FieldDefinitionGraphQL>): boolean =>
      fieldDefinitions.some((fieldDefinition) => fieldDefinition.fieldIdentifier === fromFieldId);

    const throwCouldNotDetermineSectionError = (): never => {
      assert(false, `Could not determine the section of the source field`, {
        fieldMapperConfig,
      });
    };

    let fromFieldSectionType!: TaskSchemaNodeSectionType;
    const lookupSteps: Array<LookupStepSpec> = fieldMapperConfig.lookupConfig.lookupSteps;
    if (lookupSteps.some((lookupStep) => lookupStep.type === LookupStepType.FLOWCHART_PROCESS_TASK)) {
      return {
        fromFieldSectionType: TaskSchemaNodeSectionType.MAP_FROM_START,
        fromFieldId,
      };
    }
    if (fieldMapperConfig.mapsFromType === ProcessMapperTokenType.PROCESS_OUTPUT) {
      if (lookupSteps[0].type === LookupStepType.PROCESS_TASK) {
        assert(
          isFromFieldInFieldDefinitions(inputFieldDefinitions),
          'Expected the source field to be a valid field in the task schema.'
        );
        fromFieldSectionType = TaskSchemaNodeSectionType.INPUTS;
      } else if (isFromFieldInFieldDefinitions(outputFieldDefinitions)) {
        fromFieldSectionType = TaskSchemaNodeSectionType.OUTPUTS;
      } else {
        throwCouldNotDetermineSectionError();
      }
    } else if (isFromFieldInFieldDefinitions(inputFieldDefinitions)) {
      fromFieldSectionType = TaskSchemaNodeSectionType.INPUTS;
    } else {
      throwCouldNotDetermineSectionError();
    }

    return {fromFieldSectionType, fromFieldId};
  }

  private setFallbackTransitionSources(): void {
    for (const node of this.nodesStaticData) {
      if (!isStaticTaskSchemaNodeData(node)) {
        continue;
      }

      const downstreamMappableNodeIds = findAllDownstreamMappableNodeIds(
        {nodes: this.nodesStaticData.map((n) => ({id: n.id, data: n})), edges: this.edgesStaticData},
        node.id
      );

      for (const downstreamMappableNodeId of downstreamMappableNodeIds) {
        const downstreamNode = this.getTaskSchemaNodeById(downstreamMappableNodeId);
        if (!downstreamNode) {
          continue;
        }

        const sourcePTDVId = node.taskDefinitionVersionSpec.clientId;
        const targetPTDVId = downstreamNode.taskDefinitionVersionSpec.clientId;

        const transition = this.getOrCreateTransition([sourcePTDVId], targetPTDVId);

        // Set default source if not already set by a mapper config
        if (transition.transitionSourcesBySourcePTDVId[sourcePTDVId] == null) {
          const defaultSource = getDefaultTransitionSource(node.id, this.nodesStaticData);
          transition.transitionSourcesBySourcePTDVId[sourcePTDVId] = defaultSource;
        }

        // Populate legacy field for backwards compatibility
        const nodeIdPair = getTransitionSourceByNodeIdPairKey(node.id, downstreamMappableNodeId);
        if (this.transitionSourceByNodeIdPair[nodeIdPair] == null) {
          this.transitionSourceByNodeIdPair[nodeIdPair] =
            transition.transitionSourcesBySourcePTDVId[sourcePTDVId];
        }
      }
    }
  }

  private getTransitionSourceFromMapperTokenType(mapperTokenType: ProcessMapperTokenType): TransitionSource {
    return mapperTokenType === ProcessMapperTokenType.PROCESS_OUTPUT
      ? TransitionSource.OUTPUTS
      : TransitionSource.INPUTS;
  }

  private updateNodesWithMapFromStartFields(): void {
    const nodesStaticData = this.nodesStaticData;
    const rootNodeData = this.getRootNode();
    if (!rootNodeData) {
      throw new Error('No root node found');
    }
    const rootNodeSection = rootNodeData.sections.find(
      (section) => section.sectionType === TaskSchemaNodeSectionType.INPUTS
    );
    if (!rootNodeSection) {
      throw new Error('No inputs section on root node found');
    }
    for (const node of nodesStaticData) {
      if (
        !isStaticTaskSchemaNodeData(node) ||
        node.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS ||
        node.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
      ) {
        continue;
      }

      const mapFromStartSection = node.sections.find(
        (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
      );

      if (!mapFromStartSection) {
        throw new Error('MAP_FROM_START section should exist (created by adapter)');
      }

      const sectionRows: Array<TaskSchemaNodeRowData> = [];
      const mapFromStartFieldIds = this.mapFromStartFieldIdsByNode[node.id];
      if (mapFromStartFieldIds) {
        for (const fieldId of mapFromStartFieldIds) {
          const rootNodeFieldRow = rootNodeSection.sectionRows.find((row) => row.clientId === fieldId);
          if (!rootNodeFieldRow) {
            throw new Error('Map from start field not found on root node');
          }
          sectionRows.push(convertRootRowToMapFromStartRow(rootNodeFieldRow));
        }
      }

      mapFromStartSection.sectionRows = sectionRows;
    }
  }

  private getDidInitialConfigHaveConditionalMapping(): boolean {
    return (this.flowchartConfigVersion?.schemaMapperConfigs ?? []).some((config) =>
      config.schemaFieldMapperConfigs.some((fmc) => fmc.resultMustBeNonNullToMap)
    );
  }
}

export default function buildStaticDataFromFlowchartConfigGraphQL(
  args: BuildStaticDataFromFlowchartConfigGraphQLRunnerArgs
): StaticFlowchartData {
  const runner = new BuildStaticDataFromFlowchartConfigGraphQLRunner(args);
  return runner.run();
}
